(function () {
/**
 * Entry point: publish the analytics API onto `window`.
 *
 * The callers are gateme/media/user2.js -- a global-scope classic script -- and
 * inline <script> blocks in user/show_event.html, show_event_popup.html and
 * show_event_embed.html. None of them can `import`, so `window` is the
 * interface, and this block is the whole of what they may call. Anything not
 * listed here is internal and free to change.
 *
 * user/base.html loads this bundle as a plain classic script immediately before
 * main.js and user2.js, near the end of <body>. Classic scripts execute in
 * document order and all of them before DOMContentLoaded, so these assignments
 * have happened before user2.js runs and before any $(document).ready handler
 * fires. See the README: do not make this tag async, defer or type="module".
 */ /**
 * The checkout funnel, and what each step is called at each destination.
 *
 * `as const` plus the `AnalyticEventName` union is the point of having typed
 * this at all: a call site naming a step that does not exist stops compiling,
 * where before it read `AnalyticEvents[typo]['meta']` and threw at runtime, in
 * the buyer's browser, on the one page nobody watches the console on.
 */ const $a6da2b49c4898f59$export$9575b9f845a31cb = {
    /** The buyer has added a product to the cart. */ add_to_cart: {
        gtm: 'gateme.add_to_cart',
        ga4: 'add_to_cart',
        meta: 'AddToCart'
    },
    /** The buyer has pressed the Buy Now button. */ begin_checkout: {
        gtm: 'gateme.begin_checkout',
        ga4: 'begin_checkout',
        meta: 'InitiateCheckout'
    },
    /**
   * Express checkout only -- the purchaser had no account -- and fired once
   * they have successfully given their name and e-mail.
   */ add_contact_info: {
        gtm: 'gateme.add_contact_info',
        ga4: 'add_contact_info',
        meta: 'CompleteRegistration'
    },
    /** A payment method was clicked. */ payment_method_selected: {
        gtm: 'gateme.payment_method_selected',
        ga4: 'payment_method_selected',
        // Custom event, not one of the standard ones:
        // https://developers.facebook.com/docs/meta-pixel/reference/#standard-events
        meta: 'PaymentMethodSelected'
    },
    /** Pay Now pressed; the buyer is on their way to the payment processor. */ to_payment_processor: {
        gtm: 'gateme.to_payment_processor',
        ga4: 'add_payment_info',
        meta: 'AddPaymentInfo'
    },
    /** The purchase completed and the buyer is looking at their tickets. */ purchase: {
        gtm: 'gateme.purchase',
        ga4: 'purchase',
        meta: 'Purchase'
    }
};


/**
 * Meta says not to send the same event twice in one page view, so we do not.
 * Per page view, deliberately: this resets on navigation, and is a separate
 * concern from the purchase claim in claims.ts, which persists across one.
 */ const $5cbf41a0466904a0$var$metaEventsFired = [];
function $5cbf41a0466904a0$export$b417a5944d1d002d(eventName, payload) {
    const event = (0, $a6da2b49c4898f59$export$9575b9f845a31cb)[eventName].meta;
    if (!window.fbq || $5cbf41a0466904a0$var$metaEventsFired.indexOf(event) !== -1) return;
    window.fbq('track', event, payload);
    $5cbf41a0466904a0$var$metaEventsFired.push(event);
}
function $5cbf41a0466904a0$export$e7d04a55cf9a3dae(eventName, params) {
    // g4_id as well as gtag: GTM injects a `gtag` of its own, so its presence
    // does not mean the organiser configured a GA4 property for us to send to.
    if (!window.gtag || !window.g4_id) return;
    window.gtag('event', (0, $a6da2b49c4898f59$export$9575b9f845a31cb)[eventName].ga4, params || {});
}
function $5cbf41a0466904a0$export$6b57a5b9c2725dbf(eventName, params) {
    if (!window.dataLayer) return;
    const data = {
        event: (0, $a6da2b49c4898f59$export$9575b9f845a31cb)[eventName].gtm
    };
    // Alongside `event`, never replacing it. The organiser's GTM triggers key on
    // the event name; a parameter that overwrote it would silently unhook every
    // tag they have built, and nothing on our side would look wrong.
    Object.keys(params || {}).forEach((key)=>{
        if (key !== 'event') data[key] = params[key];
    });
    window.dataLayer.push(data);
}
function $5cbf41a0466904a0$export$62c019124994993d(eventName, extraData = {}) {
    $5cbf41a0466904a0$export$b417a5944d1d002d(eventName, extraData.fbq);
    $5cbf41a0466904a0$export$e7d04a55cf9a3dae(eventName, extraData.params);
    $5cbf41a0466904a0$export$6b57a5b9c2725dbf(eventName, extraData.params);
}
function $5cbf41a0466904a0$export$73d0a139caca1fee(category, eventName, attrs) {
    if (window.mixpanel) window.mixpanel.track(category + ': ' + eventName, attrs || {});
}
const $5cbf41a0466904a0$export$60871e6df9deb521 = 300;
function $5cbf41a0466904a0$export$55bdc17e818126b3(callback) {
    window.setTimeout(callback, $5cbf41a0466904a0$export$60871e6df9deb521);
}


/**
 * Reporting a completed purchase has to happen exactly once, and only in the
 * browser that actually did the buying.
 *
 * It used to be decided server-side by Purchase.is_fresh(): fire only if the
 * money had moved in the last 15 seconds. That held while every provider
 * created the MoneyTransaction in the request that then redirected the buyer
 * back to us, so the window had a single hop to cover. Stripe's hosted Checkout
 * added a second processor -- the checkout.session.completed webhook usually
 * settles the payment while the buyer is still on Stripe's page -- so the
 * window was spent on Stripe's redirect and our page load before they ever
 * arrived, and the sale went uncounted. Intermittently, which is why it was
 * reported as "some of them" (GATE-512).
 *
 * So the browser CLAIMS the purchase on its way out to the payment provider,
 * which is the moment it certainly knows a purchase is happening, and spends
 * that claim when the buyer lands back on a result page. A result URL that is
 * bookmarked, shared, or synced to another device carries no claim -- or a
 * spent one -- and reports nothing. That is the case "was this recent?" could
 * never tell apart from the happy path: it counted a bookmark opened within 15
 * seconds and missed a real buyer who took 16.
 */ const $403b82891db72948$var$CLAIMS_KEY = 'gateme.purchase_claims';
/**
 * One entry per purchase this browser has paid for. Generous for any real
 * buyer, and it bounds what we leave behind in their storage.
 */ const $403b82891db72948$var$CLAIMS_MAX = 20;
function $403b82891db72948$var$isClaim(value) {
    return typeof value === 'object' && value !== null && typeof value.id === 'string';
}
/**
 * The claim key, always as a string.
 *
 * The two sides disagree about type and always will: user2.js hands over
 * `data.purchase_id` straight from JSON, which is a NUMBER, while the result page
 * renders it into a template literal, which is a STRING. With neither coerced,
 * `findClaim`'s === never matched and isClaim() filtered the stored claim out on
 * read, so nothing was ever reported -- the exact bug this module exists to fix,
 * reintroduced the moment the key changed from the hashid (a string) to the id.
 *
 * Coerced here rather than at the call sites because there are four of them
 * across three languages, and the next one will get it wrong too.
 */ function $403b82891db72948$var$claimKey(purchaseId) {
    return String(purchaseId);
}
function $403b82891db72948$var$readClaims() {
    try {
        const stored = JSON.parse(window.localStorage.getItem($403b82891db72948$var$CLAIMS_KEY) || 'null');
        // Anything else under our key -- an older format, or another script -- is
        // treated as absent rather than trusted.
        return Array.isArray(stored) ? stored.filter($403b82891db72948$var$isClaim) : [];
    } catch (_e) {
        // Private mode, blocked site data, or unparseable JSON.
        return [];
    }
}
function $403b82891db72948$var$writeClaims(claims) {
    try {
        window.localStorage.setItem($403b82891db72948$var$CLAIMS_KEY, JSON.stringify(claims.slice(-$403b82891db72948$var$CLAIMS_MAX)));
    } catch (_e) {
    // Storage unavailable or full. Nothing to do here; both callers behave
    // correctly when a claim cannot be stored -- see trackPurchaseOnce.
    }
}
function $403b82891db72948$var$findClaim(claims, key) {
    for(let i = 0; i < claims.length; i += 1){
        if (claims[i].id === key) return claims[i];
    }
    return null;
}
function $403b82891db72948$export$4f11fee94bbd1329(purchaseId) {
    if (!purchaseId) return;
    const key = $403b82891db72948$var$claimKey(purchaseId);
    const claims = $403b82891db72948$var$readClaims();
    if ($403b82891db72948$var$findClaim(claims, key)) // Already claimed -- the buyer came back and paid again after a declined
    // card, say. The existing claim stands: if it has been spent it stays
    // spent, so a second attempt on an already-reported purchase cannot reopen
    // it.
    return;
    claims.push({
        id: key,
        reported: false
    });
    $403b82891db72948$var$writeClaims(claims);
}
function $403b82891db72948$export$169699f6ec7cc427(purchaseId, extraData = {}) {
    if (!purchaseId) return;
    const claims = $403b82891db72948$var$readClaims();
    const claim = $403b82891db72948$var$findClaim(claims, $403b82891db72948$var$claimKey(purchaseId));
    if (!claim) // This browser never paid for it: a bookmarked or shared result URL, or the
    // same URL opened on a second device. The browser that did buy has already
    // reported it.
    //
    // A buyer whose storage is unavailable also lands here, because their claim
    // could not be written on the way out either. That costs their event.
    // Inflating every organiser's revenue would cost more.
    return;
    if (claim.reported) return;
    // Spent before the event goes out, not after: if the page is reloaded while
    // the beacons are still in flight, losing one event beats sending two. The
    // claim is this browser's own record and nothing else can spend it.
    claim.reported = true;
    $403b82891db72948$var$writeClaims(claims);
    (0, $5cbf41a0466904a0$export$62c019124994993d)('purchase', extraData);
}




const $02b93625c9d29f8a$export$644d8ea042df96a6 = {
    trackEvent: $5cbf41a0466904a0$export$73d0a139caca1fee,
    trackUserEvent: $5cbf41a0466904a0$export$62c019124994993d,
    claimPurchaseForTracking: $403b82891db72948$export$4f11fee94bbd1329,
    trackPurchaseOnce: $403b82891db72948$export$169699f6ec7cc427,
    gatemeAfterTracking: $5cbf41a0466904a0$export$55bdc17e818126b3
};
window.trackEvent = (0, $5cbf41a0466904a0$export$73d0a139caca1fee);
window.trackUserEvent = (0, $5cbf41a0466904a0$export$62c019124994993d);
window.claimPurchaseForTracking = (0, $403b82891db72948$export$4f11fee94bbd1329);
window.trackPurchaseOnce = (0, $403b82891db72948$export$169699f6ec7cc427);
window.gatemeAfterTracking = (0, $5cbf41a0466904a0$export$55bdc17e818126b3);
// The same functions under one name, for new code and for poking at it in the
// console without having to know which globals we happen to have taken.
window.__gatemeAnalytics = $02b93625c9d29f8a$export$644d8ea042df96a6;

})();
//# sourceMappingURL=index.js.map
